import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cohortsApi } from '../../../../services/api/cohorts';
import { HealthSummaryHeader } from './HealthSummaryHeader';
import { HealthTierSection } from './HealthTierSection';
import { AddressAlertModal } from './AddressAlertModal';
import { ViewNotesModal } from './ViewNotesModal';
import { TaskModal } from '../TaskModal';
import type { HealthAlert, HealthSummaryResponse } from '../../../../types';
import classes from './CohortHealthSummary.module.css';

interface CohortHealthSummaryProps {
  cohortId: string;
  healthSummary: HealthSummaryResponse;
  onRefresh: () => Promise<void>;
  onHealthChange?: () => void;
  onOpenFollowUps?: (options?: { sessionId?: string; participantId?: string }) => void;
}

export function CohortHealthSummary({ cohortId, healthSummary, onRefresh, onHealthChange, onOpenFollowUps }: CohortHealthSummaryProps) {
  const navigate = useNavigate();
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskDefaults, setTaskDefaults] = useState<{ title: string; description: string } | null>(null);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [addressAlert, setAddressAlert] = useState<HealthAlert | null>(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesAlert, setNotesAlert] = useState<HealthAlert | null>(null);

  const handleDismiss = async (alert: HealthAlert) => {
    try {
      await cohortsApi.dismissHealthAlert(cohortId, {
        ruleKey: alert.ruleKey,
        entityId: alert.participantId ?? alert.entityId,
        triggerSnapshot: {
          statValue: alert.statValue,
          title: alert.title,
          description: alert.description,
        },
      });
      await onRefresh();
      onHealthChange?.();
    } catch {
      // Error handled silently — alert stays visible
    }
  };

  const handleOpenAddress = (alert: HealthAlert) => {
    setAddressAlert(alert);
    setAddressModalOpen(true);
  };

  const handleConfirmAddress = async (alert: HealthAlert, note?: string) => {
    try {
      setAddressLoading(true);
      await cohortsApi.addressHealthAlert(cohortId, {
        ruleKey: alert.ruleKey,
        entityId: alert.participantId ?? alert.entityId,
        note,
        triggerSnapshot: {
          statValue: alert.statValue,
          title: alert.title,
          description: alert.description,
        },
      });
      setAddressModalOpen(false);
      setAddressAlert(null);
      await onRefresh();
      onHealthChange?.();
    } catch {
      // Error handled silently
    } finally {
      setAddressLoading(false);
    }
  };

  const handleAddTask = (alert: HealthAlert) => {
    setTaskDefaults({
      title: alert.title,
      description: `${alert.description}\n\nSuggested action: ${alert.suggestedAction}`,
    });
    setTaskModalOpen(true);
  };

  const handleDismissImprovement = async (alert: HealthAlert) => {
    if (!alert.historyId) return;
    try {
      await cohortsApi.dismissImprovementAlert(cohortId, alert.historyId);
      await onRefresh();
      onHealthChange?.();
    } catch {
      // Error handled silently
    }
  };

  const handleRestore = async (alert: HealthAlert) => {
    try {
      await cohortsApi.restoreHealthAlert(cohortId, {
        ruleKey: alert.ruleKey,
        entityId: alert.participantId ?? alert.entityId,
      });
      await onRefresh();
      onHealthChange?.();
    } catch {
      // Error handled silently
    }
  };

  const handleViewNotes = (alert: HealthAlert) => {
    setNotesAlert(alert);
    setNotesModalOpen(true);
  };

  const handleViewEntity = (alert: HealthAlert) => {
    const base = `/cohorts/${cohortId}`;

    // Notification-specific routing by ruleKey
    if (alert.tier === 'notification') {
      switch (alert.ruleKey) {
        case 'notification_intake_progress':
          navigate(`${base}?tab=participants`);
          return;
        case 'notification_coach_contracts':
          navigate(`${base}?tab=coaches`);
          return;
        case 'notification_groups_matched':
          navigate(`${base}?tab=participants&view=groups`);
          return;
        case 'notification_sessions_ready':
          navigate(`${base}?tab=sessions`);
          return;
        case 'notification_attendance_follow_ups':
          onOpenFollowUps?.();
          return;
        case 'notification_modality_unannounced':
          // Route to sessions tab — the announce callout will be visible on the relevant modality tab
          navigate(`${base}?tab=sessions`);
          return;
        case 'notification_coach_overdue':
          // MED-618: Route to session detail for overdue attendance/feedback
          navigate(`${base}?tab=sessions&sessionId=${alert.entityId}&sessionTab=post_session`);
          return;
        case 'notification_coach_survey_overdue':
          // MED-618: Route to coaches tab for overdue survey
          navigate(`${base}?tab=coaches`);
          return;
        case 'notification_compensation_not_configured':
          // MED-663: Route to compensation sub-tab
          navigate(`${base}?tab=coaches`);
          return;
      }
    }

    // MED-488: Coach dissatisfaction alerts route to Sessions tab filtered by that coach's group
    if (alert.ruleKey === 'midpoint_coach_dissatisfied' || alert.ruleKey === 'endpoint_coach_dissatisfied') {
      navigate(`${base}?tab=sessions&coachName=${encodeURIComponent(alert.entityName)}`);
      return;
    }

    // Default entity-type routing for risk/monitor/on_track
    switch (alert.entityType) {
      case 'participant':
        navigate(`${base}?tab=participants&participantUserId=${alert.entityId}&detailTab=activity`);
        break;
      case 'group':
        navigate(`${base}?tab=sessions&groupId=${alert.entityId}`);
        break;
      case 'session':
        navigate(`${base}?tab=sessions&sessionId=${alert.entityId}&sessionTab=post_session`);
        break;
      case 'coach':
        navigate(`${base}?tab=coaches`);
        break;
      case 'cohort':
      default:
        navigate(base);
        break;
    }
  };

  // MED-394: Check for alerts excluding notification-only scenarios
  const hasHealthAlerts =
    healthSummary.tiers.risk.alerts.length > 0 ||
    healthSummary.tiers.monitor.alerts.length > 0 ||
    healthSummary.tiers.onTrack.alerts.length > 0;
  const hasNotifications = healthSummary.tiers.notifications.alerts.length > 0;
  const isNoData = healthSummary.overallStatus === 'no_data';

  return (
    <>
      <div className={classes.container}>
        <HealthSummaryHeader
          statusMessage={healthSummary.statusMessage}
          metrics={healthSummary.metrics}
          recentSessions={healthSummary.recentSessions ?? []}
          isNoData={isNoData}
          cohortId={cohortId}
        />

        {/* MED-394: When no completed sessions, show only notifications (if any) */}
        {isNoData && hasNotifications && (
          <HealthTierSection
            tier="notification"
            data={healthSummary.tiers.notifications}
            label="Action Items"
            dotColor="#3B82F6"
            onViewEntity={handleViewEntity}
          />
        )}

        {isNoData && !hasNotifications && (
          <div className={classes.emptyState}>
            No health data available yet. Alerts will appear as sessions are completed.
          </div>
        )}

        {!isNoData && (hasHealthAlerts || hasNotifications) && (
          <>
            <HealthTierSection
              tier="risk"
              data={healthSummary.tiers.risk}
              label="Risks"
              dotColor="#D64545"
              onDismiss={handleDismiss}
              onAddress={handleOpenAddress}
              onAddTask={handleAddTask}
              onViewEntity={handleViewEntity}
              onRestore={handleRestore}
              onViewNotes={handleViewNotes}
            />
            <HealthTierSection
              tier="monitor"
              data={healthSummary.tiers.monitor}
              label="Monitor"
              dotColor="var(--medley-flag-text, #E07B4C)"
              onDismiss={handleDismiss}
              onAddress={handleOpenAddress}
              onAddTask={handleAddTask}
              onViewEntity={handleViewEntity}
              onRestore={handleRestore}
              onViewNotes={handleViewNotes}
            />
            <HealthTierSection
              tier="on_track"
              data={healthSummary.tiers.onTrack}
              label="On Track"
              dotColor="#4A7C59"
              onViewEntity={handleViewEntity}
              onDismissImprovement={handleDismissImprovement}
            />
            <HealthTierSection
              tier="notification"
              data={healthSummary.tiers.notifications}
              label="Action Items"
              dotColor="#3B82F6"
              onViewEntity={handleViewEntity}
            />

          </>
        )}

        {!isNoData && !hasHealthAlerts && !hasNotifications && (
          <div className={classes.emptyState}>
            No health data available yet. Alerts will appear as sessions are completed.
          </div>
        )}
      </div>

      <TaskModal
        opened={taskModalOpen}
        onClose={() => {
          setTaskModalOpen(false);
          setTaskDefaults(null);
        }}
        cohortId={cohortId}
        defaultTitle={taskDefaults?.title}
        defaultDescription={taskDefaults?.description}
        onSaved={() => {
          setTaskModalOpen(false);
          setTaskDefaults(null);
        }}
      />

      <AddressAlertModal
        opened={addressModalOpen}
        onClose={() => {
          setAddressModalOpen(false);
          setAddressAlert(null);
        }}
        alert={addressAlert}
        onConfirm={handleConfirmAddress}
        loading={addressLoading}
      />

      <ViewNotesModal
        opened={notesModalOpen}
        onClose={() => {
          setNotesModalOpen(false);
          setNotesAlert(null);
        }}
        alert={notesAlert}
      />
    </>
  );
}
